/**
 * React Query Hook for Customer Meetings
 * Provides data fetching, mutations, and cache management for meetings
 * WITH OFFLINE SUPPORT: Automatically falls back to IndexedDB when offline
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import {
  getCachedMeetings,
  getCachedMeetingsByCustomer,
  getCachedMeetingsByDateRange,
} from "@/lib/offline/db";
import {
  createOfflineMeeting,
  getCustomerOfflineMeetings,
  removeOfflineMeeting,
  updateOfflineMeeting,
} from "@/lib/offline/meeting-storage";
import type { OfflineMeeting } from "@/lib/offline/types";
import type {
  Meeting,
  MeetingFormData,
  MeetingListResponse,
  MeetingQueryParams,
  MeetingResponse,
  MeetingWithCustomer,
} from "@/types/meeting.types";

const API_BASE_URL = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787";

/**
 * Fetch all meetings for a specific customer
 * WITH OFFLINE SUPPORT: Falls back to IndexedDB when offline
 */
export function useCustomerMeetings(customerId: number | string) {
  return useQuery({
    queryKey: ["meetings", "customer", customerId],
    queryFn: async (): Promise<Meeting[]> => {
      try {
        // Try network first
        const response = await fetch(
          `${API_BASE_URL}/api/v1/meetings/customers/${customerId}/meetings`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch customer meetings");
        }

        const data: { data: Meeting[] } = await response.json();
        return data.data;
      } catch (error) {
        console.log("[Meetings Hook] Network failed, checking offline storage...");

        // Network failed - check IndexedDB
        const cachedMeetings = await getCachedMeetingsByCustomer(Number(customerId));
        const offlineMeetings = await getCustomerOfflineMeetings(Number(customerId));

        // Convert offline meetings to Meeting format
        const offlineAsMeetings: Meeting[] = offlineMeetings.map((om) => ({
          id: om.id as any, // Will be UUID string
          customer_id: om.customer_id,
          project_id: om.project_id,
          meeting_date: om.meeting_date,
          meeting_time: om.meeting_time,
          duration_minutes: om.duration_minutes,
          event_type: om.event_type,
          meeting_type: om.meeting_type,
          status: om.status,
          location: om.location,
          notes: om.notes,
          created_at: om.created_at,
          updated_at: om.updated_at,
        }));

        // Merge cached + offline meetings
        const allMeetings = [...cachedMeetings, ...offlineAsMeetings];

        if (allMeetings.length === 0) {
          throw error; // Re-throw if no offline data
        }

        console.log(`[Meetings Hook] Serving ${allMeetings.length} meetings from offline storage`);
        return allMeetings as Meeting[];
      }
    },
    enabled: Boolean(customerId),
  });
}

/**
 * Fetch all meetings with optional filters
 */
export function useMeetings(params?: MeetingQueryParams) {
  const queryString = params
    ? "?" +
      Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join("&")
    : "";

  return useQuery({
    queryKey: ["meetings", params],
    queryFn: async (): Promise<MeetingListResponse> => {
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings${queryString}`);

      if (!response.ok) {
        throw new Error("Failed to fetch meetings");
      }

      return response.json();
    },
  });
}

/**
 * Fetch a single meeting by ID
 */
export function useMeeting(meetingId: number | string) {
  return useQuery({
    queryKey: ["meetings", meetingId],
    queryFn: async (): Promise<MeetingWithCustomer> => {
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`);

      if (!response.ok) {
        throw new Error("Failed to fetch meeting");
      }

      const data: MeetingResponse = await response.json();
      return data.data as MeetingWithCustomer;
    },
    enabled: Boolean(meetingId),
  });
}

/**
 * Fetch meetings for calendar view (date range)
 * WITH OFFLINE SUPPORT: Falls back to IndexedDB when offline
 */
export function useCalendarMeetings(dateFrom: string, dateTo: string, customerId?: number) {
  const queryParams = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });

  if (customerId) {
    queryParams.append("customer_id", String(customerId));
  }

  return useQuery({
    queryKey: ["meetings", "calendar", dateFrom, dateTo, customerId],
    queryFn: async (): Promise<MeetingWithCustomer[]> => {
      try {
        // Try network first
        const response = await fetch(`${API_BASE_URL}/api/v1/meetings/calendar?${queryParams}`);

        if (!response.ok) {
          throw new Error("Failed to fetch calendar meetings");
        }

        const data: { data: MeetingWithCustomer[] } = await response.json();
        return data.data;
      } catch (error) {
        console.log("[Calendar Hook] Network failed, checking offline storage...");

        // Network failed - check IndexedDB
        let cachedMeetings = await getCachedMeetingsByDateRange(dateFrom, dateTo);

        // Filter by customer if specified
        if (customerId) {
          cachedMeetings = cachedMeetings.filter((m) => m.customer_id === customerId);
        }

        // Get offline meetings in date range
        // If no customerId, get ALL offline meetings, otherwise filter by customer
        const allOfflineMeetings = customerId
          ? await getCustomerOfflineMeetings(customerId)
          : await import("@/lib/offline/db").then((m) => m.getAllOfflineMeetings());
        const offlineInRange = allOfflineMeetings.filter(
          (om) => om.meeting_date >= dateFrom && om.meeting_date <= dateTo
        );

        // Convert offline meetings to MeetingWithCustomer format
        const offlineAsMeetings: MeetingWithCustomer[] = offlineInRange.map((om) => ({
          id: om.id as any,
          customer_id: om.customer_id,
          customer_name: "Offline Customer", // Placeholder - would need customer lookup
          customer_email: "",
          project_id: om.project_id,
          meeting_date: om.meeting_date,
          meeting_time: om.meeting_time,
          duration_minutes: om.duration_minutes,
          event_type: om.event_type,
          meeting_type: om.meeting_type,
          status: om.status,
          location: om.location,
          notes: om.notes,
          created_at: om.created_at,
          updated_at: om.updated_at,
        }));

        // Merge cached + offline meetings
        const allMeetings = [...cachedMeetings, ...offlineAsMeetings];

        if (allMeetings.length === 0) {
          throw error; // Re-throw if no offline data
        }

        console.log(`[Calendar Hook] Serving ${allMeetings.length} meetings from offline storage`);
        return allMeetings as MeetingWithCustomer[];
      }
    },
    enabled: Boolean(dateFrom && dateTo),
  });
}

/**
 * Create a new meeting
 * WITH OFFLINE SUPPORT: Saves offline when network unavailable, syncs later
 */
export function useCreateMeeting() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async (meetingData: MeetingFormData): Promise<Meeting> => {
      console.log("[Create Meeting] Starting mutation with data:", meetingData);

      // If offline, save to IndexedDB
      if (!isOnline) {
        console.log("[Create Meeting] Offline - saving to IndexedDB");
        const result = await createOfflineMeeting(meetingData);

        if (!(result.success && result.meeting)) {
          throw new Error(result.error || "Failed to save meeting offline");
        }

        // Convert OfflineMeeting to Meeting format
        return {
          id: result.meeting.id as any,
          customer_id: result.meeting.customer_id,
          project_id: result.meeting.project_id,
          meeting_date: result.meeting.meeting_date,
          meeting_time: result.meeting.meeting_time,
          duration_minutes: result.meeting.duration_minutes,
          meeting_type: result.meeting.meeting_type,
          status: result.meeting.status,
          location: result.meeting.location,
          notes: result.meeting.notes,
          created_at: result.meeting.created_at,
          updated_at: result.meeting.updated_at,
        };
      }

      // Online - normal API call
      console.log("[Create Meeting] Online - calling API");
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(meetingData),
      });

      console.log("[Create Meeting] API response status:", response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error("[Create Meeting] API error:", error);
        throw new Error(error.error || "Failed to create meeting");
      }

      const data: MeetingResponse = await response.json();
      console.log("[Create Meeting] API success, response data:", data);
      return data.data as Meeting;
    },
    onSuccess: (data) => {
      console.log("[Create Meeting] Success! Meeting data:", data);

      // Invalidate customer meetings query
      queryClient.invalidateQueries({
        queryKey: ["meetings", "customer", data.customer_id],
      });
      // Invalidate calendar queries
      queryClient.invalidateQueries({ queryKey: ["meetings", "calendar"] });
      // Invalidate all meetings query
      queryClient.invalidateQueries({ queryKey: ["meetings"] });

      console.log("[Create Meeting] Queries invalidated, calendar should refresh");

      if (isOnline) {
        toast({
          title: "Meeting Scheduled",
          description: "The meeting has been successfully scheduled.",
        });
      } else {
        toast({
          title: "Meeting Saved Offline",
          description: "Will sync when connection restored.",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create meeting.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Update an existing meeting
 */
export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      meetingId,
      updates,
    }: {
      meetingId: number | string;
      updates: Partial<MeetingFormData>;
    }): Promise<Meeting> => {
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update meeting");
      }

      const data: MeetingResponse = await response.json();
      return data.data as Meeting;
    },
    onSuccess: (data) => {
      // Invalidate specific meeting query
      queryClient.invalidateQueries({ queryKey: ["meetings", String(data.id)] });
      // Invalidate customer meetings query
      queryClient.invalidateQueries({
        queryKey: ["meetings", "customer", data.customer_id],
      });
      // Invalidate all meetings query
      queryClient.invalidateQueries({ queryKey: ["meetings"] });

      toast({
        title: "Meeting Updated",
        description: "The meeting has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update meeting.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Delete a meeting
 * WITH OFFLINE SUPPORT: Handles offline-created meetings immediately
 */
export function useDeleteMeeting() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isOnline = useOnlineStatus();

  return useMutation({
    mutationFn: async ({
      meetingId,
      customerId,
    }: {
      meetingId: number | string;
      customerId: number;
    }): Promise<void> => {
      const meetingIdStr = String(meetingId);

      // Check if this is an offline meeting (UUID format)
      const isOfflineMeeting = meetingIdStr.includes("-") || meetingIdStr.startsWith("offline_");

      if (isOfflineMeeting) {
        // Delete from IndexedDB only
        console.log("[Delete Meeting] Deleting offline meeting:", meetingId);
        const result = await removeOfflineMeeting(meetingIdStr);
        if (!result.success) {
          throw new Error(result.error || "Failed to delete offline meeting");
        }
        return;
      }

      // If offline and not an offline meeting, we can't delete from server
      if (!isOnline) {
        throw new Error("Cannot delete synced meetings while offline");
      }

      // Online - normal API call
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete meeting");
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate customer meetings query
      queryClient.invalidateQueries({
        queryKey: ["meetings", "customer", variables.customerId],
      });
      // Invalidate calendar queries
      queryClient.invalidateQueries({ queryKey: ["meetings", "calendar"] });
      // Invalidate all meetings query
      queryClient.invalidateQueries({ queryKey: ["meetings"] });

      toast({
        title: "Meeting Deleted",
        description: "The meeting has been successfully deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete meeting.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Update meeting status (complete, cancel, reschedule)
 */
export function useUpdateMeetingStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      meetingId,
      status,
      notes,
    }: {
      meetingId: number | string;
      status: "completed" | "cancelled" | "rescheduled" | "scheduled";
      notes?: string;
    }): Promise<Meeting> => {
      const response = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status, ...(notes && { notes }) }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update meeting status");
      }

      const data: MeetingResponse = await response.json();
      return data.data as Meeting;
    },
    onSuccess: (data, variables) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["meetings"] });

      const statusMessages = {
        completed: "Meeting marked as completed",
        cancelled: "Meeting has been cancelled",
        rescheduled: "Meeting has been rescheduled",
        scheduled: "Meeting rescheduled and confirmed",
      };

      toast({
        title: "Status Updated",
        description: statusMessages[variables.status],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update meeting status.",
        variant: "destructive",
      });
    },
  });
}
